import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { cores } from '../../theme';
import { storage } from '../../firebase/config';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import {
    Typography, Container, Box, Paper, TextField, Button, Alert, CircularProgress,
    IconButton, Avatar, Fade, Snackbar, useTheme, Grid, Card, CardMedia, CardActions,
    Divider, LinearProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import {
    ArrowBack,
    Save as SaveIcon,
    Assignment as AssignmentIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Image as ImageIcon,
    Upload as UploadIcon,
    Add as AddIcon,
    Warning as WarningIcon
} from '@mui/icons-material';

const CasoEditarPagina = () => {
    const { id } = useParams();
    const navegar = useNavigate();
    const theme = useTheme();
    const [titulo, setTitulo] = useState('');
    const [descricao, setDescricao] = useState('');
    const [imagensAtuais, setImagensAtuais] = useState([]);
    const [novosArquivos, setNovosArquivos] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [processandoUpload, setProcessandoUpload] = useState(false);
    const [progresso, setProgresso] = useState(0);
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState('');
    const [confirmarExclusao, setConfirmarExclusao] = useState({ aberto: false, imagemId: null });

    const buscarCaso = async () => {
        try {
            const resposta = await api.get(`/api/casos-clinicos/${id}`);
            const caso = resposta.data;
            setTitulo(caso.titulo);
            setDescricao(caso.descricao || '');
            setImagensAtuais(caso.imagens || []);
        } catch (err) {
            setErro("Não foi possível carregar os dados do caso clínico.");
        } finally {
            setCarregando(false);
        }
    };

    useEffect(() => {
        buscarCaso();
    }, [id]);

    const lidarComSubmissao = async (e) => {
        e.preventDefault();
        setErro('');
        setSucesso('');

        if (!titulo) {
            setErro('O campo "Título" é obrigatório.');
            return;
        }

        try {
            const dadosAtualizados = { titulo, descricao };
            await api.put(`/api/casos-clinicos/${id}`, dadosAtualizados);
            setSucesso('Caso clínico atualizado com sucesso!');
        } catch (err) {
            setErro(err.response?.data?.erro || 'Ocorreu um erro ao atualizar o caso.');
        }
    };

    const abrirConfirmacaoExclusao = (imagemId) => {
        setConfirmarExclusao({ aberto: true, imagemId });
    };

    const fecharConfirmacaoExclusao = () => {
        setConfirmarExclusao({ aberto: false, imagemId: null });
    };

    const lidarComDelecaoImagem = async () => {
        try {
            await api.delete(`/api/casos-clinicos/imagens/${confirmarExclusao.imagemId}`);
            setImagensAtuais(imagensAtuais.filter(img => img.id !== confirmarExclusao.imagemId));
            setSucesso('Imagem removida com sucesso!');
            fecharConfirmacaoExclusao();
        } catch (err) {
            setErro('Erro ao excluir a imagem.');
            fecharConfirmacaoExclusao();
        }
    };

    const lidarComSelecaoDeNovasImagens = (e) => {
        const arquivosSelecionados = Array.from(e.target.files);
        setNovosArquivos(prevArquivos => [...prevArquivos, ...arquivosSelecionados]);
    };

    const removerNovoArquivo = (index) => {
        setNovosArquivos(prevArquivos => prevArquivos.filter((_, i) => i !== index));
    };

    const lidarComUploadDeNovasImagens = async () => {
        if (novosArquivos.length === 0) return;

        setProcessandoUpload(true);
        setProgresso(0);
        setErro('');

        try {
            const uploadPromises = novosArquivos.map(arquivo => {
                return new Promise((resolve, reject) => {
                    const nomeArquivo = `${Date.now()}-${arquivo.name}`;
                    const storageRef = ref(storage, `casos-clinicos/${nomeArquivo}`);
                    const uploadTask = uploadBytesResumable(storageRef, arquivo);

                    uploadTask.on('state_changed',
                        (snapshot) => {
                            const prog = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setProgresso(prog);
                        },
                        (error) => {
                            console.error("Erro no upload:", error);
                            reject(error);
                        },
                        async () => {
                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                            resolve(downloadURL);
                        }
                    );
                });
            });

            const novasUrls = await Promise.all(uploadPromises);
            await api.post(`/api/casos-clinicos/${id}/imagens`, { imagem_urls: novasUrls });

            setSucesso('Novas imagens enviadas com sucesso!');
            setNovosArquivos([]);
            buscarCaso();
        } catch (err) {
            setErro("Ocorreu um erro durante o upload das novas imagens.");
        } finally {
            setProcessandoUpload(false);
        }
    };

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    return (
        <>
            <Snackbar
                open={!!sucesso}
                autoHideDuration={4000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={() => setSucesso('')}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    sx={{
                        width: '100%',
                        boxShadow: cores.sombras.medio,
                        backgroundColor: cores.sucesso.principal
                    }}
                >
                    {sucesso}
                </Alert>
            </Snackbar>

            <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
                <Fade in={true} timeout={500}>
                    <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                        <Box component="form" onSubmit={lidarComSubmissao}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 4,
                                p: { xs: 2, sm: 3 },
                                borderRadius: 2,
                                background: cores.gradientes.primario,
                                color: 'white'
                            }}>
                                <IconButton
                                    onClick={() => navegar('/gerenciar-casos')}
                                    sx={{
                                        mr: 2,
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.3)',
                                        }
                                    }}
                                >
                                    <ArrowBack />
                                </IconButton>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="h4"
                                        component="h1"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                            lineHeight: 1.2
                                        }}
                                    >
                                        Editar Caso Clínico
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            opacity: 0.9,
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}
                                    >
                                        Atualize as informações do caso
                                    </Typography>
                                </Box>
                            </Box>

                            <Fade in={true} timeout={600}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: { xs: 2, sm: 3 },
                                        mb: 3,
                                        bgcolor: '#f8fafc',
                                        borderRadius: 2,
                                        border: `1px solid ${cores.cinza.claro}`,
                                        boxShadow: cores.sombras.suave
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                        <Avatar
                                            sx={{
                                                mr: 2,
                                                bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                color: cores.primaria.principal
                                            }}
                                        >
                                            <EditIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="overline" sx={{ color: cores.cinza.medio, fontWeight: 600 }}>
                                                INFORMAÇÕES DO CASO
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: cores.cinza.escuro }}>
                                                Atualize o título e a descrição do caso clínico
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Fade>

                            <Box sx={{ p: { xs: 0, sm: 2 } }}>
                                <TextField
                                    label="Título do Caso Clínico"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    value={titulo}
                                    onChange={(e) => setTitulo(e.target.value)}
                                    sx={{ mb: 3 }}
                                    InputProps={{
                                        sx: { borderRadius: 2 }
                                    }}
                                    helperText="Exemplo: Diagnóstico de paciente com dor abdominal"
                                />

                                <TextField
                                    label="Descrição Completa do Caso"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    rows={10}
                                    value={descricao}
                                    onChange={(e) => setDescricao(e.target.value)}
                                    InputProps={{
                                        sx: { borderRadius: 2 }
                                    }}
                                    helperText="Descreva em detalhes o caso clínico, incluindo histórico, sintomas e exames relevantes"
                                />

                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        sx={{
                                            borderRadius: 2,
                                            py: 1.5,
                                            px: 3,
                                            bgcolor: cores.primaria.principal,
                                            fontWeight: 600,
                                            boxShadow: cores.sombras.suave,
                                            '&:hover': {
                                                bgcolor: cores.primaria.escuro,
                                            }
                                        }}
                                    >
                                        Salvar Alterações no Texto
                                    </Button>
                                </Box>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        <Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    color: cores.primaria.principal,
                                    mb: 3,
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                }}
                            >
                                Gerenciamento de Imagens
                            </Typography>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    mb: 4,
                                    borderRadius: 2,
                                    border: `1px solid ${cores.cinza.claro}`,
                                    backgroundColor: 'rgba(241, 245, 249, 0.6)',
                                }}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2
                                }}>
                                    <Avatar
                                        sx={{
                                            mr: 2,
                                            bgcolor: `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.1)`,
                                            color: cores.secundaria.principal
                                        }}
                                    >
                                        <ImageIcon />
                                    </Avatar>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: cores.secundaria.principal }}>
                                        Imagens Atuais
                                    </Typography>
                                </Box>

                                <Divider sx={{ mb: 3 }} />

                                {imagensAtuais.length > 0 ? (
                                    <Grid container spacing={2}>
                                        {imagensAtuais.map((imagem) => (
                                            <Grid item key={imagem.id} xs={12} sm={6} md={4} lg={3}>
                                                <Card
                                                    sx={{
                                                        borderRadius: 2,
                                                        overflow: 'hidden',
                                                        boxShadow: cores.sombras.suave,
                                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: cores.sombras.medio,
                                                        }
                                                    }}
                                                >
                                                    <CardMedia
                                                        component="img"
                                                        height="160"
                                                        image={imagem.imagem_url}
                                                        alt="Imagem do caso clínico"
                                                        sx={{ objectFit: 'cover' }}
                                                    />
                                                    <CardActions sx={{ justifyContent: 'flex-end', p: 1 }}>
                                                        <IconButton
                                                            onClick={() => abrirConfirmacaoExclusao(imagem.id)}
                                                            size="small"
                                                            sx={{
                                                                color: cores.erro.principal,
                                                                bgcolor: `rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.1)`,
                                                                '&:hover': {
                                                                    bgcolor: `rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.2)`,
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <Box sx={{ p: 2, textAlign: 'center', color: cores.cinza.medio }}>
                                        <Typography>Nenhuma imagem cadastrada para este caso.</Typography>
                                    </Box>
                                )}
                            </Paper>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 2,
                                    border: `1px dashed ${cores.cinza.medio}`,
                                    backgroundColor: 'rgba(241, 245, 249, 0.6)',
                                }}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2
                                }}>
                                    <Avatar
                                        sx={{
                                            mr: 2,
                                            bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                            color: cores.primaria.principal
                                        }}
                                    >
                                        <AddIcon />
                                    </Avatar>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: cores.primaria.principal }}>
                                        Adicionar Novas Imagens
                                    </Typography>
                                </Box>

                                <Divider sx={{ mb: 3 }} />

                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<UploadIcon />}
                                        sx={{
                                            borderRadius: 2,
                                            py: 1.2,
                                            borderColor: cores.secundaria.principal,
                                            color: cores.secundaria.principal,
                                            '&:hover': {
                                                borderColor: cores.secundaria.clara,
                                                bgcolor: `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.04)`
                                            }
                                        }}
                                    >
                                        Selecionar Imagens
                                        <input type="file" hidden multiple accept="image/*" onChange={lidarComSelecaoDeNovasImagens} />
                                    </Button>
                                </Box>

                                {novosArquivos.length > 0 && (
                                    <>
                                        <Box sx={{
                                            mt: 2,
                                            p: 2,
                                            bgcolor: cores.fundo.claro,
                                            borderRadius: 2,
                                            border: `1px solid ${cores.cinza.claro}`
                                        }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    mb: 2,
                                                    color: cores.primaria.principal,
                                                    fontWeight: 600
                                                }}
                                            >
                                                {novosArquivos.length} {novosArquivos.length === 1 ? 'arquivo selecionado' : 'arquivos selecionados'} para envio:
                                            </Typography>

                                            <Grid container spacing={2}>
                                                {novosArquivos.map((arquivo, index) => (
                                                    <Grid item xs={6} sm={4} md={3} key={index}>
                                                        <Box
                                                            sx={{
                                                                position: 'relative',
                                                                borderRadius: 2,
                                                                overflow: 'hidden',
                                                                boxShadow: cores.sombras.suave,
                                                                height: 120
                                                            }}
                                                        >
                                                            <img
                                                                src={URL.createObjectURL(arquivo)}
                                                                alt={`Prévia ${index}`}
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                            <IconButton
                                                                onClick={() => removerNovoArquivo(index)}
                                                                size="small"
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 4,
                                                                    right: 4,
                                                                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                                                                    color: cores.erro.principal,
                                                                    '&:hover': {
                                                                        bgcolor: 'white'
                                                                    }
                                                                }}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                bottom: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bgcolor: 'rgba(0, 0, 0, 0.6)',
                                                                p: 0.5,
                                                                overflow: 'hidden',
                                                                whiteSpace: 'nowrap',
                                                                textOverflow: 'ellipsis'
                                                            }}>
                                                                <Typography variant="caption" sx={{ color: 'white' }}>
                                                                    {arquivo.name}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>

                                        {processandoUpload && (
                                            <Box sx={{ mt: 3 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        mb: 1,
                                                        color: cores.primaria.principal,
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    Enviando imagens... {progresso.toFixed(0)}%
                                                </Typography>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={progresso}
                                                    sx={{
                                                        height: 8,
                                                        borderRadius: 4,
                                                        bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                        '& .MuiLinearProgress-bar': {
                                                            bgcolor: cores.primaria.principal
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        )}

                                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="contained"
                                                startIcon={<UploadIcon />}
                                                disabled={processandoUpload}
                                                onClick={lidarComUploadDeNovasImagens}
                                                sx={{
                                                    borderRadius: 2,
                                                    py: 1.2,
                                                    px: 3,
                                                    bgcolor: cores.primaria.principal,
                                                    fontWeight: 600,
                                                    boxShadow: cores.sombras.suave,
                                                    '&:hover': {
                                                        bgcolor: cores.primaria.escuro,
                                                    }
                                                }}
                                            >
                                                {processandoUpload ? 'Enviando...' : 'Enviar Novas Imagens'}
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </Paper>

                            {erro && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        mt: 3,
                                        borderRadius: 2,
                                        backgroundColor: cores.erro.claro,
                                        borderColor: cores.erro.medio,
                                        color: cores.erro.escuro,
                                        '& .MuiAlert-icon': {
                                            alignItems: 'center'
                                        }
                                    }}
                                >
                                    {erro}
                                </Alert>
                            )}

                            <Box sx={{
                                mt: 4,
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navegar('/gerenciar-casos')}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1.5,
                                        px: 3,
                                        borderColor: cores.cinza.medio,
                                        color: cores.cinza.escuro,
                                        '&:hover': {
                                            borderColor: cores.cinza.escuro,
                                            bgcolor: 'rgba(71, 85, 105, 0.04)'
                                        }
                                    }}
                                >
                                    Voltar para Lista de Casos
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Fade>
            </Container>

            <Dialog
                open={confirmarExclusao.aberto}
                onClose={fecharConfirmacaoExclusao}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte,
                        maxWidth: 450
                    }
                }}
            >
                <DialogTitle sx={{
                    color: cores.erro.escuro,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700
                }}>
                    <WarningIcon color="error" />
                    Confirmar Exclusão
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2, color: cores.cinza.escuro }}>
                        Tem certeza que deseja remover esta imagem do caso clínico?
                    </DialogContentText>
                    <DialogContentText sx={{ color: cores.cinza.medio }}>
                        Esta ação não pode ser desfeita e a imagem será permanentemente excluída.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={fecharConfirmacaoExclusao}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            borderColor: cores.cinza.claro,
                            color: cores.cinza.escuro,
                            '&:hover': {
                                borderColor: cores.cinza.medio,
                                bgcolor: 'rgba(203, 213, 225, 0.1)'
                            }
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={lidarComDelecaoImagem}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.2)`,
                            '&:hover': {
                                bgcolor: cores.erro.escuro,
                            }
                        }}
                    >
                        Excluir Imagem
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CasoEditarPagina;